import { localized, msg } from '@lit/localize';
import type { ActionMenu } from '@spectrum-web-components/action-menu';
import { css, html, LitElement } from 'lit';
import type { Id, WorkflowSummary } from '../../../api/types';
import { workflowService } from '../../../services/workflow-service';

import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/action-menu/sp-action-menu.js';
import '@spectrum-web-components/menu/sp-menu-divider.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-add.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-play.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-refresh.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-delete.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-import.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-export.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-chevron-left.js';
import '@spectrum-web-components/dialog/sp-dialog.js';
import '@spectrum-web-components/underlay/sp-underlay.js';
import '@spectrum-web-components/button/sp-button.js';

@localized()
export class SbWorkflows extends LitElement {
  static styles = css`
    :host {
      display: block;
    }
    .toolbar {
      display: flex;
      flex-direction: column;
      gap: 8px;
      align-items: stretch;
    }
    .header-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .title-stack {
      flex: 1;
      min-width: 0;
    }
    .workflow-title {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: var(--spectrum-gray-900);
      font-size: 18px;
      font-weight: 600;
      line-height: 1.2;
    }
    .actions {
      display: flex;
      gap: 6px;
      align-items: center;
      flex: 0 0 auto;
    }
    .overview-button {
      flex: 0 0 auto;
      margin-top: -2px;
      --mod-actionbutton-padding-inline: 4px;
    }
    .run-button {
      --mod-actionbutton-border-color-default: rgba(63, 99, 243, 0.48);
      --mod-actionbutton-border-color-hover: var(
        --color-sidebar-selected-border
      );
      --mod-actionbutton-background-color-default: var(
        --color-sidebar-selected-bg
      );
      --mod-actionbutton-background-color-hover: #e2ebff;
      --mod-actionbutton-content-color-default: var(
        --color-sidebar-selected-border
      );
      --mod-actionbutton-content-color-hover: var(
        --color-sidebar-selected-border
      );
      font-size: 12px;
      font-weight: 600;
    }
    .confirm-underlay {
      position: fixed;
      inset: 0;
      z-index: 6;
      background-color: var(--color-modal-overlay);
    }
    .confirm-dialog {
      position: fixed;
      inset: 0;
      z-index: 7;
      display: grid;
      place-items: center;
      padding: var(--gap-4);
      box-sizing: border-box;
    }
    .confirm-dialog sp-dialog {
      background: var(--color-card-bg);
    }
    .muted {
      color: var(--color-workflows-muted-text);
      font-size: 12px;
      line-height: 1.4;
    }
  `;
  static properties = {
    baseUrl: { type: String },
    loading: { state: true },
    error: { state: true },
    items: { state: true },
    createLoading: { state: true },
    selectedWorkflowId: { type: Number },
    confirmDeleteId: { state: true },
  } as const;

  declare baseUrl?: string;
  private declare loading: boolean;
  private declare error?: string;
  private declare items: WorkflowSummary[];
  private declare createLoading: boolean;
  private declare selectedWorkflowId?: Id;
  private declare confirmDeleteId: Id | null;

  constructor() {
    super();
    this.loading = false;
    this.error = undefined;
    this.items = [];
    this.createLoading = false;
    this.confirmDeleteId = null;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.load();
  }

  async load() {
    this.loading = true;
    this.error = undefined;
    try {
      this.items = await workflowService.list(this.baseUrl);
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.loading = false;
    }
  }

  private async create() {
    this.createLoading = true;
    try {
      const newWorkflow = await workflowService.create(this.baseUrl);
      this.openInEditor(newWorkflow.id, { showTemplateChooser: true });
      await this.load();
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.createLoading = false;
    }
  }

  updateWorkflowName(id: Id, name: string | null) {
    const normalizedName =
      typeof name === 'string' && name.trim().length > 0 ? name.trim() : null;

    this.items = this.items.map((item) =>
      item.id === id ? { ...item, name: normalizedName } : item
    );
  }

  private triggerImport = () => {
    const input = this.renderRoot.querySelector<HTMLInputElement>(
      '#workflow-import-input'
    );
    input?.click();
  };

  private async handleImportChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    this.error = undefined;

    try {
      const workflow = await workflowService.import(file, this.baseUrl);
      this.openInEditor(workflow.id);
      await this.load();
    } catch (err) {
      this.error = err instanceof Error ? err.message : String(err);
    } finally {
      target.value = '';
    }
  }

  private async exportSelected() {
    if (this.selectedWorkflowId == null) {
      this.error = msg('Select an automation to export.');
      return;
    }

    this.error = undefined;

    try {
      const { blob, filename } = await workflowService.export(
        this.selectedWorkflowId,
        this.baseUrl
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename ?? `workflow-${this.selectedWorkflowId}.json`;
      anchor.rel = 'noopener';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : msg('Failed to export automation');
    }
  }

  private async removeWorkflow(id: number) {
    const prev = this.items;
    this.items = this.items.filter((i) => i.id !== id);
    try {
      await workflowService.remove(id, this.baseUrl);
      if (this.items.length > 0) {
        const lastItem = this.items[this.items.length - 1];
        this.openInEditor(lastItem.id);
      }
    } catch (e) {
      this.items = prev;
      this.error = e instanceof Error ? e.message : String(e);
    }
    if (this.selectedWorkflowId === id) this.selectedWorkflowId = undefined;
  }

  private run() {
    this.dispatchEvent(
      new CustomEvent('run-workflow', { bubbles: true, composed: true })
    );
  }
  private reset() {
    this.dispatchEvent(
      new CustomEvent('reset-workflow', { bubbles: true, composed: true })
    );
  }
  private deleteSelected() {
    if (this.selectedWorkflowId != null) {
      this.confirmDeleteId = this.selectedWorkflowId;
    }
  }

  private cancelDelete = () => {
    this.confirmDeleteId = null;
  };

  private confirmDelete = () => {
    if (this.confirmDeleteId == null) return;
    const id = this.confirmDeleteId;
    this.confirmDeleteId = null;
    this.removeWorkflow(id);
  };

  private formatWorkflowName(id: Id) {
    const workflow = this.items.find((item) => item.id === id);
    if (!workflow) return `${msg('Automation')} ${id}`;
    const name =
      typeof workflow.name === 'string' && workflow.name.trim().length > 0
        ? workflow.name.trim()
        : null;
    return name ?? `${msg('Automation')} ${workflow.id}`;
  }

  private showOverview = () => {
    this.dispatchEvent(
      new CustomEvent('show-overview', { bubbles: true, composed: true })
    );
  };

  private handleOverflowAction = (event: Event) => {
    const menu = event.currentTarget as ActionMenu | null;
    switch (menu?.value) {
      case 'create':
        this.create();
        break;
      case 'delete':
        this.deleteSelected();
        break;
      case 'reset':
        this.reset();
        break;
      case 'import':
        this.triggerImport();
        break;
      case 'export':
        this.exportSelected();
        break;
      default:
        break;
    }
  };

  render() {
    const selectedWorkflowTitle =
      this.selectedWorkflowId == null
        ? this.loading
          ? msg('Loading automations...')
          : msg('No automation selected')
        : this.formatWorkflowName(this.selectedWorkflowId);

    return html`
      <div class="toolbar">
        <div class="header-row">
          <sp-action-button
            class="overview-button"
            quiet
            label=${msg('Back to overview')}
            @click=${this.showOverview}
            data-testid="overview-back-button"
          >
            <sp-icon-chevron-left slot="icon"></sp-icon-chevron-left>
          </sp-action-button>
          <div class="title-stack">
            <div class="workflow-title" title=${selectedWorkflowTitle}>
              ${selectedWorkflowTitle}
            </div>
          </div>
          <div class="actions" role="group" aria-label=${msg('Automation actions')}>
            <sp-action-button
              class="run-button"
              data-testid="run-workflow-button"
              label=${msg('Run automation')}
              title=${msg('Run automation')}
              @click=${this.run}
              ?disabled=${!this.selectedWorkflowId}
            >
              <sp-icon-play slot="icon"></sp-icon-play>
              ${msg('Run')}
            </sp-action-button>
            <sp-action-menu
              quiet
              size="m"
              label=${msg('More actions')}
              title=${msg('More actions')}
              @change=${this.handleOverflowAction}
            >
              <sp-menu-item value="create" ?disabled=${this.createLoading}>
                ${
                  this.createLoading
                    ? html`<sp-progress-circle
                        slot="icon"
                        label=${msg('Creating automation')}
                        indeterminate
                      ></sp-progress-circle>`
                    : html`<sp-icon-add slot="icon"></sp-icon-add>`
                }
                ${msg('New automation')}
              </sp-menu-item>
              <sp-menu-item value="delete" ?disabled=${!this.selectedWorkflowId}>
                <sp-icon-delete slot="icon"></sp-icon-delete>
                ${msg('Delete automation')}
              </sp-menu-item>
              <sp-menu-item value="reset" ?disabled=${!this.selectedWorkflowId}>
                <sp-icon-refresh slot="icon"></sp-icon-refresh>
                ${msg('Reset automation')}
              </sp-menu-item>
              <sp-menu-divider></sp-menu-divider>
              <sp-menu-item value="import">
                <sp-icon-import slot="icon"></sp-icon-import>
                ${msg('Import')}
              </sp-menu-item>
              <sp-menu-item value="export" ?disabled=${!this.selectedWorkflowId}>
                <sp-icon-export slot="icon"></sp-icon-export>
                ${msg('Export')}
              </sp-menu-item>
            </sp-action-menu>
          </div>
        </div>
        <input
          id="workflow-import-input"
          type="file"
          accept="application/json"
          hidden
          @change=${this.handleImportChange}
        />
        ${this.error ? html`<span class="muted">${this.error}</span>` : ''}
      </div>
      ${
        this.confirmDeleteId != null
          ? html`
            <sp-underlay
              class="confirm-underlay"
              open
              @click=${this.cancelDelete}
            ></sp-underlay>
            <div class="confirm-dialog">
              <sp-dialog open underlay size="m" role="alertdialog">
                <h2 slot="heading">${msg('Delete automation')}</h2>
                <p>
                  ${msg('Delete')} ${this.formatWorkflowName(this.confirmDeleteId)}?
                  ${msg('This action cannot be undone.')}
                </p>
                <sp-button slot="button" variant="secondary" @click=${this.cancelDelete}>
                  ${msg('Cancel')}
                </sp-button>
                <sp-button slot="button" variant="negative" @click=${this.confirmDelete}>
                  ${msg('Delete')}
                </sp-button>
              </sp-dialog>
            </div>
          `
          : ''
      }
    `;
  }

  private openInEditor(
    id: number,
    options: { showTemplateChooser?: boolean } = {}
  ) {
    this.selectedWorkflowId = id;
    this.dispatchEvent(
      new CustomEvent('open-workflow', {
        detail: {
          id,
          showTemplateChooser: options.showTemplateChooser ?? false,
        },
        bubbles: true,
        composed: true,
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sb-workflows': SbWorkflows;
  }
}

customElements.define('sb-workflows', SbWorkflows);
